import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, KeyRound, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogoFull } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

// /reset-password (no token → request a link ; ?token=… → choose a new password)
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const request = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const reset = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setUser(data);
      toast.success("Password updated — you are signed in");
      navigate("/dashboard", { replace: true });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid-bg bg-background flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 space-y-6">
        <LogoFull width={200} />
        {token ? (
          <form onSubmit={reset} className="space-y-4" data-testid="reset-form">
            <h1 className="font-display text-2xl font-bold flex items-center gap-2"><KeyRound className="text-primary" size={20} /> Choose a new password</h1>
            <div className="space-y-1.5"><Label htmlFor="pw">New password</Label><Input id="pw" type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="pw2">Confirm password</Label><Input id="pw2" type="password" minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-input" /></div>
            <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold glow-primary" data-testid="reset-submit-button">{loading ? <Loader2 className="animate-spin" size={18} /> : "Update password"}</Button>
          </form>
        ) : sent ? (
          <div className="space-y-3" data-testid="reset-sent">
            <h1 className="font-display text-2xl font-bold flex items-center gap-2"><MailCheck className="text-primary" size={20} /> Check your inbox</h1>
            <p className="text-sm text-muted-foreground">If an account exists for <b className="text-foreground">{email}</b>, we sent a link valid for 30 minutes. Accounts using Google Sign-In receive a reminder instead.</p>
            <Link to="/login" className="text-sm text-primary hover:underline" data-testid="reset-back-login">← Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={request} className="space-y-4" data-testid="forgot-form">
            <h1 className="font-display text-2xl font-bold flex items-center gap-2"><KeyRound className="text-primary" size={20} /> Forgot your password?</h1>
            <p className="text-sm text-muted-foreground">Enter your email and we will send you a secure link to choose a new one.</p>
            <div className="space-y-1.5"><Label htmlFor="em">Email</Label><Input id="em" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@studio.com" required data-testid="forgot-email-input" /></div>
            <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold glow-primary" data-testid="forgot-submit-button">{loading ? <Loader2 className="animate-spin" size={18} /> : "Send reset link"}</Button>
            <Link to="/login" className="block text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-back-login">← Back to sign in</Link>
          </form>
        )}
      </div>
    </div>
  );
}
